import { type FuelType } from "@/hooks/use-fuel";
import { FEATURES } from "@/config/features";
import { motion } from "framer-motion";
import { useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface FuelSelectorProps {
  selected: FuelType;
  onSelect: (type: FuelType) => void;
}

const ALL_FUEL_TYPES: FuelType[] = ["U91", "E10", "P95", "P98", "DL", "PDL", "LPG", "EV"];

// Filter fuel types based on feature flags
const FUEL_TYPES: FuelType[] = ALL_FUEL_TYPES.filter(type => {
  if (type === "EV" && !FEATURES.EV_ENABLED) {
    return false;
  }
  return true;
});

const FUEL_LABELS: Record<FuelType, string> = {
  E10: "E10",
  U91: "U91",
  P95: "P95",
  P98: "P98",
  DL: "Diesel",
  PDL: "P.Diesel",
  LPG: "LPG",
  E85: "E85",
  B20: "B20",
  EV: "EV",
};

export function FuelSelector({ selected, onSelect }: FuelSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<FuelType, HTMLButtonElement>>(new Map());

  const scrollToSelected = useCallback((type: FuelType) => {
    const button = buttonRefs.current.get(type);
    if (button && scrollRef.current) {
      const container = scrollRef.current;
      // Only scroll if content overflows the container
      if (container.scrollWidth <= container.clientWidth) return;
      const buttonRect = button.getBoundingClientRect();
      const scrollLeft = button.offsetLeft - container.offsetWidth / 2 + buttonRect.width / 2;
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToSelected(selected);
  }, [selected, scrollToSelected]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 150;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleSelect = (type: FuelType) => {
    onSelect(type);
    scrollToSelected(type);
  };

  return (
    <div className="w-full py-4 overflow-hidden relative">
      <button 
        type="button"
        onClick={() => scroll('left')}
        className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-background/90 border border-white/10 z-20 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-colors shadow-lg lg:hidden"
        aria-label="Scroll left"
        data-testid="fuel-scroll-left"
      >
        <ChevronLeft size={18} />
      </button>
      <button 
        type="button"
        onClick={() => scroll('right')}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-background/90 border border-white/10 z-20 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-colors shadow-lg lg:hidden"
        aria-label="Scroll right"
        data-testid="fuel-scroll-right"
      >
        <ChevronRight size={18} />
      </button>
      
      <div 
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto px-12 no-scrollbar snap-x lg:flex-wrap lg:justify-center lg:overflow-visible lg:px-0"
      >
        {FUEL_TYPES.map((type) => {
          const isSelected = selected === type;
          return (
            <button
              type="button"
              key={type}
              ref={(el) => { if (el) buttonRefs.current.set(type, el); }}
              onClick={() => handleSelect(type)}
              data-testid={`fuel-selector-${type}`}
              className={`
                relative flex-shrink-0 px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 snap-center
                ${isSelected 
                  ? "text-background shadow-lg" 
                  : "bg-secondary/40 text-muted-foreground hover:bg-secondary/60 hover:text-foreground border border-white/5"}
              `}
            >
              {isSelected && (
                <motion.div
                  layoutId="activeFuelPill"
                  className="absolute inset-0 bg-primary rounded-full z-0"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              <span className="relative z-10 font-mono tracking-tight">{FUEL_LABELS[type]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
